import React, { useState, useEffect, useCallback } from 'react';
import { Box, Container, Paper, Typography, Stepper, Step, StepLabel, Button, Alert, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OnboardingService, createOnboardingService } from '../services/onboardingService';
import { OnboardingState, OnboardingStep, PetProfile, OnboardingCallbacks } from '../types/onboarding';
import { WelcomeStep } from './onboarding/WelcomeStep';
import { PhotoUploadStep } from './onboarding/PhotoUploadStep';
import { ConfirmDetailsStep } from './onboarding/ConfirmDetailsStep';
import { CompletionStep } from './onboarding/CompletionStep';

interface OnboardingWizardProps {
  userId: string;
  userLanguage?: 'en' | 'zh-CN' | 'es' | 'fr';
  onComplete?: (petProfile: PetProfile) => void;
  onCancel?: () => void;
  onError?: (error: string) => void;
  skipWelcomeStep?: boolean;
}

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  userId,
  userLanguage = 'en',
  onComplete,
  onCancel,
  onError,
  skipWelcomeStep = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Language-specific content
  const getContent = () => {
    const content = {
      en: {
        title: "PetSnap Onboarding",
        subtitle: "Create your pet's profile",
        initializing: "Initializing...",
        processing: "Processing...",
        unknownStep: "Unknown step",
        retry: "Retry",
        steps: {
          welcome: "Welcome",
          photoUpload: "Upload Photo",
          confirmDetails: "Confirm Details",
          completed: "Completed"
        },
        fallbackDescription: "Your {petType} looks wonderful! Please provide the details manually in the next step."
      },
      'zh-CN': {
        title: "PetSnap引导",
        subtitle: "创建您的宠物资料",
        initializing: "正在初始化...",
        processing: "处理中...",
        unknownStep: "未知步骤",
        retry: "重试",
        steps: {
          welcome: "欢迎",
          photoUpload: "上传照片",
          confirmDetails: "确认详情",
          completed: "完成"
        },
        fallbackDescription: "您的{petType}看起来很棒！请在下一步中手动提供详细信息。"
      },
      es: {
        title: "Incorporación a PetSnap",
        subtitle: "Crea el perfil de tu mascota",
        initializing: "Inicializando...",
        processing: "Procesando...",
        unknownStep: "Paso desconocido",
        retry: "Reintentar",
        steps: {
          welcome: "Bienvenido",
          photoUpload: "Subir Foto",
          confirmDetails: "Confirmar Detalles",
          completed: "Completado"
        },
        fallbackDescription: "¡Tu {petType} se ve maravilloso! Por favor proporciona los detalles manualmente en el siguiente paso."
      },
      fr: {
        title: "Intégration PetSnap",
        subtitle: "Créez le profil de votre animal",
        initializing: "Initialisation...",
        processing: "Traitement...",
        unknownStep: "Étape inconnue",
        retry: "Réessayer",
        steps: {
          welcome: "Bienvenue",
          photoUpload: "Télécharger Photo",
          confirmDetails: "Confirmer Détails",
          completed: "Terminé"
        },
        fallbackDescription: "Votre {petType} a l'air merveilleux ! Veuillez fournir les détails manuellement à l'étape suivante."
      }
    };

    return content[userLanguage] || content.en;
  };

  const content = getContent();
  const [service, setService] = useState<OnboardingService | null>(null);
  const [state, setState] = useState<OnboardingState | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Initialize onboarding service
  useEffect(() => {
    const callbacks: OnboardingCallbacks = {
      onComplete: (petProfile) => {
        onComplete?.(petProfile);
      },
      onCancel: () => {
        onCancel?.();
      },
      onError: (error) => {
        onError?.(error);
      },
      onStepChange: (step) => {
        // State will be updated via the subscription
      }
    };

    const onboardingService = createOnboardingService({}, callbacks);
    setService(onboardingService);

    // Show UI immediately
    setState(onboardingService.getState());
    setIsInitializing(false);

    onboardingService.initialize(userId, userLanguage)
      .then(() => {
        // If skipWelcomeStep is true, start onboarding directly (skip welcome step)
        if (skipWelcomeStep) {
          onboardingService.startOnboarding();
        }
        // Update state after initialization is complete
        setState(onboardingService.getState());
      })
      .catch((error) => {
        console.error('Failed to initialize onboarding service:', error);
        setState(onboardingService.getState());
      });

    // Cleanup
    return () => {
      onboardingService.reset();
    };
  }, [userId, userLanguage, onComplete, onCancel, onError, skipWelcomeStep]);

  // Subscribe to state changes
  useEffect(() => {
    if (!service) return;

    const updateState = () => {
      setState(service.getState());
    };

    // Simple polling for state updates (in a real app, you might use an event emitter)
    const interval = setInterval(updateState, 500);

    return () => clearInterval(interval);
  }, [service]);

  const handleStartOnboarding = useCallback(() => {
    service?.startOnboarding();
  }, [service]);

  const handlePhotoUpload = useCallback(async (file: File, petType: 'dog' | 'cat') => {
    if (!service) return;

    try {
      await service.uploadPetPhoto(file, petType);
    } catch (error) {
      console.error('Photo upload failed:', error);
    }
  }, [service]);

  const handleSkipPhoto = useCallback(async (petType: 'dog' | 'cat') => {
    if (!service) return;

    try {
      console.log('🔄 Skipping photo upload for pet type:', petType);

      // Update pet type
      await service.updatePetData({ type: petType });

      // Create fallback analysis data for skipped photo
      const fallbackAge = 2; // Default age for fallback
      const fallbackAnalysis = {
        breed: 'Unknown',
        confidence: 0.1,
        age: fallbackAge,
        ageConfidence: 0.1,
        sex: 'unknown' as const,
        sexConfidence: 0.1,
        description: content.fallbackDescription.replace('{petType}', petType),
        analysisImageUrl: ''
      };

      // Also update pet data with calculated birthday
      try {
        const { calculateBirthdayFromAge } = await import('../utils/birthdayCalculation');
        const calculatedBirthday = calculateBirthdayFromAge(fallbackAge);
        await service.updatePetData({
          birthday: calculatedBirthday,
          age: fallbackAge
        });
        console.log('🎂 Set fallback birthday for skipped photo:', calculatedBirthday.toDate().toLocaleDateString());
      } catch (error) {
        console.warn('⚠️ Failed to calculate fallback birthday:', error);
      }

      // Apply fallback analysis
      service['applyAnalysisResults'](fallbackAnalysis);

      // Move directly to confirm details step
      service['updateStep'](OnboardingStep.CONFIRM_DETAILS);

      console.log('✅ Photo upload skipped, moved to confirm details step');
    } catch (error) {
      console.error('❌ Skip photo failed:', error);
    }
  }, [service, content.fallbackDescription]);

  const handleUpdatePetData = useCallback(async (updates: Partial<PetProfile>) => {
    if (!service) return;

    try {
      await service.updatePetData(updates);
    } catch (error) {
      console.error('Failed to update pet data:', error);
    }
  }, [service]);

  const handleCompleteOnboarding = useCallback(async (petName: string) => {
    if (!service) return;

    try {
      await service.completeOnboarding(petName);
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
  }, [service]);

  const handleCancel = useCallback(() => {
    console.log('🔄 OnboardingWizard: Cancel button clicked');
    service?.cancelOnboarding();
  }, [service]);

  // Loading state
  if (isInitializing || !state || !service) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography variant="h6">
            {content.initializing}
          </Typography>
        </Box>
      </Container>
    );
  }

  // Error state
  if (state.error) {
    return (
      <Container maxWidth="md">
        <Box p={3}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {state.error}
          </Alert>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
          >
            {content.retry}
          </Button>
        </Box>
      </Container>
    );
  }

  // Step definitions for the stepper
  const steps = [
    content.steps.welcome,
    content.steps.photoUpload,
    content.steps.confirmDetails,
    content.steps.completed
  ];

  const getStepContent = () => {
    switch (state.currentStep) {
      case OnboardingStep.WELCOME:
        return (
          <WelcomeStep
            onStart={handleStartOnboarding}
            onCancel={handleCancel}
            userLanguage={state.userLanguage}
          />
        );

      case OnboardingStep.PHOTO_UPLOAD:
        return (
          <PhotoUploadStep
            onPhotoUpload={handlePhotoUpload}
            onSkipPhoto={handleSkipPhoto}
            onCancel={handleCancel}
            isLoading={state.isLoading}
            userLanguage={state.userLanguage}
          />
        );

      case OnboardingStep.CONFIRM_DETAILS:
        return (
          <ConfirmDetailsStep
            petData={state.petData}
            onUpdateData={handleUpdatePetData}
            onComplete={handleCompleteOnboarding}
            onCancel={handleCancel}
            isLoading={state.isLoading}
            userLanguage={state.userLanguage}
          />
        );

      case OnboardingStep.COMPLETED:
        return (
          <CompletionStep
            petData={state.petData}
            onAddAnotherPet={() => {
              // Reset and start over for another pet
              service.reset();
              service.initialize(userId, userLanguage);
            }}
            onCancel={handleCancel}
            userLanguage={state.userLanguage}
          />
        );

      default:
        return (
          <Box p={3}>
            <Typography variant="h6" color="error">
              {content.unknownStep}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Container maxWidth={isMobile ? "sm" : "md"}>
      <Box py={isMobile ? 2 : 4}>
        {/* Header */}
        <Box mb={isMobile ? 2 : 4} textAlign="center">
          <Typography
            variant={isMobile ? "h5" : "h4"}
            component="h1"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            {content.title}
          </Typography>
          <Typography
            variant={isMobile ? "body1" : "subtitle1"}
            color="text.secondary"
            sx={{ px: isMobile ? 2 : 0 }}
          >
            {content.subtitle}
          </Typography>
        </Box>

        {/* Progress Stepper */}
        <Paper elevation={2} sx={{ p: isMobile ? 2 : 3, mb: isMobile ? 2 : 4 }}>
          <Stepper
            activeStep={state.currentStep - 1}
            alternativeLabel={!isMobile}
            orientation={isMobile ? "vertical" : "horizontal"}
            sx={{
              '& .MuiStepLabel-root': {
                '& .MuiStepLabel-label': {
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                },
              },
            }}
          >
            {steps.map((label, index) => (
              <Step key={index}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Step Content */}
        <Paper elevation={2} sx={{ p: isMobile ? 2 : 3 }}>
          {getStepContent()}
        </Paper>

        {/* Loading overlay */}
        {state.isLoading && (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgcolor="rgba(255, 255, 255, 0.8)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={9999}
          >
            <Paper elevation={4} sx={{ p: isMobile ? 2 : 3, textAlign: 'center', mx: 2 }}>
              <Typography variant={isMobile ? "body1" : "h6"}>
                {content.processing}
              </Typography>
            </Paper>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default OnboardingWizard;
